import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { FuellyLogo } from "@/components/FuellyBrand";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PLATFORM, LEGAL_INFO } from "@/data/platform";
import { localeTag } from "@/i18n";
import { LEGAL } from "@/constants/testIds";

/**
 * The two public documents at /privacy and /terms.
 *
 * One component renders both. They share a layout, a language switcher and a
 * date, and the only thing that differs is which list of sections to walk — two
 * screens would be two places to get the same thing wrong.
 *
 * They live outside `TenantApp`, beside `Landing`: these are Fuelly's documents,
 * not a restaurant's, so they must open without a slug. That also makes them
 * linkable from somewhere a guest has no tenant context — which is the whole
 * reason they exist, since Google will not publish an OAuth consent screen
 * without a privacy policy at a stable public URL.
 *
 * The section order is data, not markup: adding a disclosure means one entry
 * here and three catalog keys, and `check_translations.py` fails the build if
 * any language is missing one. That gate matters more here than anywhere else
 * in the app — a privacy policy that silently falls back to English is a
 * disclosure the reader never received in a language they read.
 */
const SECTIONS = {
  privacy: ["who", "account", "profile", "orders", "device", "restaurant", "ai",
            "analytics", "notCollected", "keeping", "contact"],
  terms: ["what", "ordering", "prices", "ai", "allergens", "account",
          "availability", "changes", "contact"],
};

const Legal = ({ doc }) => {
  const { t } = useTranslation();
  const updated = new Date(LEGAL_INFO.updated).toLocaleDateString(localeTag(), {
    year: "numeric", month: "long", day: "numeric",
  });

  // Every body string may name the operator, its address or the domain. They are
  // passed to all of them rather than to the few that use them today, so adding
  // the name to a paragraph later is a catalog edit and not a code change.
  const vars = {
    entity: LEGAL_INFO.entity,
    email: LEGAL_INFO.contactEmail,
    domain: PLATFORM.domain,
    platform: PLATFORM.name,
  };

  // Deliberately outside `.app-frame`. That container is the storefront's 28rem
  // phone frame, which is right for a guest at a table and wrong for a document:
  // on a desktop it draws a strip down the middle of the screen and reads as a
  // page that failed to load. These two are read on whatever the reader happens
  // to have — including by somebody reviewing what this service does — so the
  // page is fluid, and only the prose keeps a measure a person can actually read.
  return (
    <div className="min-h-screen w-full bg-[#F9F9F7]">
      {/* Wide, but not edge to edge. A line of legal prose across 1920px is about
          200 characters and nobody finishes it; the container is fluid and the
          measure is what stays bounded. */}
      <div className="mx-auto w-full max-w-4xl px-5 sm:px-8 lg:px-12 xl:px-16">
        <div data-testid={LEGAL.page(doc)} className="min-h-screen pt-8 pb-16 sm:pt-12 lg:pt-16">
          {/* A real navigation, not a <Link>: in production `/` is the static
              home page nginx serves, and a client-side route would answer the
              same URL with a different page depending on how you arrived. */}
          <a
            data-testid={LEGAL.home}
            href="/"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400"
          >
            <ArrowLeft size={14} />
            {PLATFORM.name}
          </a>

          <FuellyLogo size={40} className="mt-7 sm:mt-9" />

          <h1
            data-testid={LEGAL.title(doc)}
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-neutral-900 mt-4"
          >
            {t(`legal.${doc}.title`)}
          </h1>
          <p data-testid={LEGAL.updated} className="text-[11px] sm:text-xs text-neutral-400 mt-1.5">
            {t("legal.updated", { date: updated })}
          </p>

          {/* Type grows with the viewport rather than staying at the storefront's
              phone sizes: 13px is right in a hand and unreadable across a desk. */}
          <div className="mt-8 sm:mt-12 space-y-6 sm:space-y-8">
            {SECTIONS[doc].map((key) => (
              <section key={key} data-testid={LEGAL.section(key)}>
                <h2 className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                  {t(`legal.${doc}.${key}.heading`)}
                </h2>
                {/* Paragraphs are split on a blank line rather than held as
                    numbered keys: a translator moving from two paragraphs to
                    three should not have to add a key in three files, and a
                    renumbering would silently orphan the old ones. */}
                {t(`legal.${doc}.${key}.body`, vars).split("\n\n").map((para, i) => (
                  <p
                    key={i}
                    className="text-[13px] sm:text-[15px] leading-relaxed text-neutral-600 mt-1.5 sm:mt-2"
                  >
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-neutral-100 flex items-center justify-between gap-4">
            <Link
              to={doc === "privacy" ? "/terms" : "/privacy"}
              className="text-[12px] font-semibold text-neutral-700 underline underline-offset-2"
            >
              {t(doc === "privacy" ? "legal.terms.title" : "legal.privacy.title")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Legal;
